import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  MessageCircle,
  Send,
  Users,
  Search,
  Phone,
  Mail,
  Info,
  X,
  Minimize2,
  Maximize2,
  User,
  Clock,
  Check,
  CheckCheck
} from 'lucide-react';
import { getApiBaseUrl } from '../../config/apiBase';
import UniversalSearchInput from '../../components/common/UniversalSearchInput';

const LiveSupportChat = () => {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'users'
  const [chatMode, setChatMode] = useState('list'); // 'list' or 'conversation'
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [isMinimized, setIsMinimized] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    
    // Initialize WebSocket connection
    initWebSocket();
    
    // Fetch users list
    if (userData?.role === 'master_admin' || userData?.role === 'admin' || userData?.role === 'support') {
      fetchUsers();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    let filtered = users;
    if (searchTerm) {
      filtered = users.filter(u => 
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.username?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredUsers(filtered);
  }, [users, searchTerm]);

  const initWebSocket = () => {
    const token = localStorage.getItem('token');
    const wsUrl = `${getApiBaseUrl().replace('http', 'ws')}/ws/support`;
    
    wsRef.current = new WebSocket(`${wsUrl}?token=${token}`);
    
    wsRef.current.onopen = () => {
      console.log('Connected to support chat');
    };
    
    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleWebSocketMessage(data);
    };
    
    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current.onclose = () => {
      console.log('Disconnected from support chat');
      // Attempt to reconnect after 3 seconds
      setTimeout(initWebSocket, 3000);
    };
  };

  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'message':
        if (selectedUser && (data.from === selectedUser.id || data.to === selectedUser.id)) {
          setMessages(prev => [...prev, data]);
        }
        break;
      case 'user_list':
        setUsers(data.users);
        break;
      case 'user_online':
        setOnlineUsers(prev => new Set([...prev, data.userId]));
        break;
      case 'user_offline':
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(data.userId);
          return newSet;
        });
        break;
      case 'typing':
        setTypingUsers(prev => {
          if (data.isTyping) {
            return new Set([...prev, data.userId]);
          } else {
            const newSet = new Set(prev);
            newSet.delete(data.userId);
            return newSet;
          }
        });
        break;
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/api/v1/support/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const sendMessage = () => {
    if (!messageInput.trim() || !selectedUser) return;
    
    const message = {
      type: 'message',
      to: selectedUser.id,
      message: messageInput.trim(),
      timestamp: new Date().toISOString()
    };
    
    wsRef.current?.send(JSON.stringify(message));
    setMessages(prev => [...prev, {
      ...message,
      from: user.id,
      sent: true
    }]);
    setMessageInput('');
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setChatMode('conversation');
    // Load chat history
    loadChatHistory(user.id);
  };

  const loadChatHistory = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/api/v1/support/chat/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleTyping = (e) => {
    setMessageInput(e.target.value);
    
    // Send typing indicator
    if (wsRef.current && selectedUser) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        to: selectedUser.id,
        isTyping: e.target.value.length > 0
      }));
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isSuperAdmin = user?.role === 'master_admin' || user?.role === 'admin';
  const isSupport = user?.role === 'support';

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageCircle className="w-6 h-6 text-indigo-600" />
            <h1 className="text-xl font-semibold text-gray-900">
              Live Support {isSuperAdmin ? 'Admin' : isSupport ? 'Agent' : 'Chat'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Tabs */}
          {(isSuperAdmin || isSupport) && (
            <div className="bg-white border-b border-gray-200">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('chat')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'chat'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <MessageCircle className="w-4 h-4 inline mr-2" />
                  Chat
                </button>
                <button
                  onClick={() => setActiveTab('users')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'users'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Users className="w-4 h-4 inline mr-2" />
                  Users ({users.length})
                </button>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 flex overflow-hidden">
            {/* Users List */}
            {((isSuperAdmin || isSupport) && activeTab === 'users') || (!isSuperAdmin && !isSupport) ? (
              <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
                <div className="p-4 border-b border-gray-200">
                  <UniversalSearchInput
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search users..."
                  />
                </div>
                <div className="flex-1 overflow-y-auto">
                  {filteredUsers.map((userItem) => (
                    <div
                      key={userItem.id}
                      onClick={() => handleUserSelect(userItem)}
                      className={`flex items-center gap-3 p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 ${
                        selectedUser?.id === userItem.id ? 'bg-indigo-50' : ''
                      }`}
                    >
                      <div className="relative">
                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-600" />
                        </div>
                        {onlineUsers.has(userItem.id) && (
                          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {userItem.name || userItem.username}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{userItem.email}</p>
                      </div>
                      <div className="text-xs text-gray-400">
                        {onlineUsers.has(userItem.id) ? 'Online' : 'Offline'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Chat Area */}
            <div className="flex-1 flex flex-col">
              {selectedUser ? (
                <>
                  {/* Chat Header */}
                  <div className="bg-white px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-gray-600" />
                          </div>
                          {onlineUsers.has(selectedUser.id) && (
                            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white"></div>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedUser.name || selectedUser.username}
                          </p>
                          <p className="text-xs text-gray-500">
                            {typingUsers.has(selectedUser.id) ? 'Typing...' : 'Active now'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                          <Phone className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                          <Mail className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                          <Info className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {messages.map((message, index) => (
                      <div
                        key={index}
                        className={`flex ${message.from === user.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs px-4 py-2 rounded-lg ${
                            message.from === user.id
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <p className="text-sm">{message.message}</p>
                          <div className={`flex items-center justify-end gap-1 mt-1 text-xs ${
                            message.from === user.id ? 'text-indigo-200' : 'text-gray-500'
                          }`}>
                            <span>{formatTime(message.timestamp)}</span>
                            {message.from === user.id && (
                              message.read ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {typingUsers.has(selectedUser.id) && (
                      <div className="flex justify-start">
                        <div className="bg-gray-100 px-4 py-2 rounded-lg">
                          <div className="flex gap-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <div className="bg-white border-t border-gray-200 px-6 py-4">
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={messageInput}
                        onChange={handleTyping}
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        placeholder="Type a message..."
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <button
                        onClick={sendMessage}
                        disabled={!messageInput.trim()}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        Send
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center bg-gray-50">
                  <div className="text-center">
                    <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {isSuperAdmin || isSupport ? 'Select a user to start chatting' : 'No active conversations'}
                    </h3>
                    <p className="text-gray-600">
                      {isSuperAdmin || isSupport 
                        ? 'Choose from the users list on the left to begin a conversation'
                        : 'Start a new conversation or wait for someone to contact you'
                      }
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LiveSupportChat;
